import React, {CSSProperties, ComponentType, ReactNode} from 'react'

import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    UniqueIdentifier,
    rectIntersection,
    useSensor,
    useSensors
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable'
import {
    restrictToVerticalAxis,
    restrictToParentElement
} from '@dnd-kit/modifiers'
import SortableItem, {DragHandle} from './SortableItem.js'

export interface SortableListProps<T> {
    items: T[]
    /** Called after a drag with the items in their new order and the new index of the dragged item */
    onChange?: (items: T[], newIndex: number) => void
    /** DragHandle is the button that drags the item when dragHandle, render it in the item */
    renderItem: (item: T, DragHandle: ComponentType, index: number) => ReactNode
    /** Only the DragHandle rendered in an item starts a drag, instead of the whole item */
    dragHandle?: boolean
    /** Items that can't be dragged */
    isDisabled?: (item: T) => boolean
    /** The id of an item, unique in the list (default item.id). indexId for items without an id (strings, duplicates) */
    getId?: (item: T, index: number) => UniqueIdentifier
    /** Style of the list (a flex column) */
    style?: CSSProperties
}

const defaultGetId = (item: unknown) => (item as {id?: UniqueIdentifier})?.id

/**
 * getId for items without an id of their own: their position. Not the bare index, dnd-kit treats an id 0 as none.
 * The items are then rendered with index keys, so a component in an item stays at its position after a reorder.
 */
export const indexId = (item: unknown, index: number) => `item${index}`

/**
 * A vertical list whose items can be reordered by dragging (mouse, touch or keyboard). Dragging the whole item
 * starts after moving 5px (by touch: after holding it 120ms), so clicks on buttons and inputs in it keep working; with
 * dragHandle only the DragHandle given to renderItem drags it. The list doesn't reorder itself: onChange gets the new
 * order.
 */
export default function SortableList<T>({style = {}, onChange, items, isDisabled, renderItem, dragHandle, getId = defaultGetId}: SortableListProps<T>) {
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                // Moving 5px before a drag starts lets clicks on buttons and inputs in the item through
                distance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 120,
                tolerance: 6
            }
        })
    )

    const list = items || []
    const ids = list.map((item, index) => getId(item, index))

    function handleDragEnd({active, over}: DragEndEvent) {
        if (over && active.id !== over.id) {
            const oldIndex = ids.indexOf(active.id)
            const newIndex = ids.indexOf(over.id)

            onChange?.(arrayMove(list, oldIndex, newIndex), newIndex)
        }
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    flex: 1,
                    ...style
                }}
            >
                <SortableContext
                    items={ids}
                    strategy={verticalListSortingStrategy}
                >
                    {list.map((item, index) => {
                        return (
                            <SortableItem key={ids[index]} id={ids[index]} disabled={isDisabled?.(item)} dragHandle={dragHandle}>
                                {renderItem(item, DragHandle, index)}
                            </SortableItem>
                        )
                    })}
                </SortableContext>
            </div>
        </DndContext>
    )
}
